import {
  dateSchema,
  objectIdSchema,
  paginationSchema,
  referralCodeSchema,
  ReferralTracking,
  referralTrackingContextSchema,
  referralTrackingTypeSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API } from '../..';

/* ─────────────────────────── public ─────────────────────────── */

/**
 * `POST /referral/tracking` — public, unauthenticated, rate-limited.
 * Records a deduped view / click on a code. Posted by `hero-r.js` on
 * external sites, by the landing site and by the dashboard.
 *
 * Fire-and-forget from the client's point of view: the response says
 * only whether the code resolved, never who owns it.
 */
export const referralTrackingCreateAPISchema = z.object({
  code: referralCodeSchema.describe('The referral code from `?r=` / the `hero_r` cookie.'),
  type: referralTrackingTypeSchema.describe('`view` on page load, `click` on a tracked CTA.'),
  visitorId: z
    .string()
    .trim()
    .min(8)
    .max(64)
    .optional()
    .describe(
      'First-party visitor id from the `hero_vid` cookie. Random, set by the client; used for dedupe and to stitch a later lead to this hit.',
    ),
  context: referralTrackingContextSchema
    .optional()
    .describe(
      'Page context — url, host, referrer, CTA target, business / product, language, UTMs.',
    ),
});
export type ReferralTrackingCreateAPI = API<
  z.infer<typeof referralTrackingCreateAPISchema>,
  {
    /** `false` for unknown / inactive codes — the hit was dropped. */
    accepted: boolean;
    /** `true` when the hit merged into an existing row inside the dedupe window. */
    deduped?: boolean;
  }
>;

/* ─────────────────────────── admin ─────────────────────────── */

/** `GET /referral/tracking/admin-all` — per-link / per-partner hit list. */
export const referralTrackingGetAllAdminAPISchema = paginationSchema.extend({
  linkId: objectIdSchema.optional().describe('Filter to one link.'),
  partnerId: objectIdSchema.optional().describe('Filter to one partner.'),
  type: referralTrackingTypeSchema.optional().describe('`view` or `click`.'),
  visitorId: z.string().trim().optional().describe('Filter to one visitor.'),
  from: dateSchema.optional().describe('`doc.createdAt >=`.'),
  to: dateSchema.optional().describe('`doc.createdAt <=`.'),
  matchedOnly: z.coerce
    .boolean()
    .optional()
    .describe('Only hits that were matched to a lead through `closest_tracking`.'),
});
export type ReferralTrackingGetAllAdminAPI = API<
  z.infer<typeof referralTrackingGetAllAdminAPISchema>,
  ReferralTracking[]
>;

/**
 * `GET /referral/tracking/admin-stats` — daily buckets of views /
 * clicks for a link or partner, for the admin chart.
 */
export const referralTrackingStatsAdminAPISchema = z.object({
  linkId: objectIdSchema.optional().describe('Scope to one link.'),
  partnerId: objectIdSchema.optional().describe('Scope to one partner.'),
  from: dateSchema.optional().describe('Window start; defaults to 30 days ago.'),
  to: dateSchema.optional().describe('Window end; defaults to now.'),
});
export type ReferralTrackingDailyBucket = {
  /** `YYYY-MM-DD`. */
  day: string;
  views: number;
  clicks: number;
  visitors: number;
};
export type ReferralTrackingStatsAdminAPI = API<
  z.infer<typeof referralTrackingStatsAdminAPISchema>,
  {
    buckets: ReferralTrackingDailyBucket[];
    totals: { views: number; clicks: number; visitors: number };
  }
>;
